import React, { useState } from 'react';
import { toolsData } from '../data/toolsData';

const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const CheckIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;

const allToolsFlat = Object.values(toolsData).flat();
const QUICK_ACCESS_LIMIT = 8;

interface EditQuickAccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentTools: string[];
    onSave: (newTools: string[]) => void;
}

const EditQuickAccessModal: React.FC<EditQuickAccessModalProps> = ({ isOpen, onClose, currentTools, onSave }) => {
    const [tempSelected, setTempSelected] = useState<string[]>(currentTools);

    const handleToggleTool = (toolName: string) => {
        const isSelected = tempSelected.includes(toolName);
        if (isSelected) {
            setTempSelected(tempSelected.filter(t => t !== toolName));
        } else {
            if (tempSelected.length < QUICK_ACCESS_LIMIT) {
                setTempSelected([...tempSelected, toolName]);
            }
        }
    };

    const handleSave = () => {
        onSave(tempSelected);
    };

    if (!isOpen) return null;

    const isLimitReached = tempSelected.length >= QUICK_ACCESS_LIMIT;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50 modal-enter" role="dialog" aria-modal="true">
            <div className="bg-gradient-to-br from-[#1a2a47] to-[#15243E] rounded-2xl border border-blue-900/50 w-full max-w-4xl text-gray-300 shadow-2xl flex flex-col max-h-[90vh]">
                <header className="p-6 border-b border-blue-900/50 flex justify-between items-center flex-shrink-0">
                    <div>
                        <h2 className="text-2xl font-bold text-white">Edit Quick Actions</h2>
                        <p className="text-gray-400 mt-1">Select up to {QUICK_ACCESS_LIMIT} tools for your dashboard widget.</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white"><CloseIcon /></button>
                </header>

                <main className="p-6 overflow-y-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {allToolsFlat.map(tool => {
                            const isSelected = tempSelected.includes(tool.name);
                            const isDisabled = !isSelected && isLimitReached;
                            const IconComponent = tool.icon;
                            return (
                                <button 
                                    key={tool.name}
                                    onClick={() => handleToggleTool(tool.name)}
                                    disabled={isDisabled}
                                    className={`relative flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                                        isSelected ? 'bg-blue-800/80 ring-2 ring-yellow-400' : 'bg-black/20 hover:bg-white/5'
                                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-[#101D35] text-yellow-400`}>
                                        <IconComponent className="h-6 w-6" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-200">{tool.name}</span>
                                    {isSelected && (
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center border-2 border-[#1a2a47]">
                                            <CheckIcon />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </main>

                <footer className="p-6 border-t border-blue-900/50 mt-auto flex justify-between items-center flex-shrink-0">
                    <div className={`text-sm font-semibold ${isLimitReached ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {tempSelected.length} / {QUICK_ACCESS_LIMIT} Selected
                    </div>
                    <div className="flex gap-4">
                        <button onClick={onClose} className="px-6 py-2 rounded-lg text-gray-300 font-semibold hover:bg-white/10 transition-colors">Cancel</button>
                        <button onClick={handleSave} className="bg-yellow-400 text-gray-900 font-bold py-2 px-6 rounded-lg hover:bg-yellow-300 transition-transform hover:scale-105">Save Changes</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default EditQuickAccessModal;